import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/stores/auth';
import { AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { friendlyError } from '@/lib/friendlyError';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Min 8 characters'),
});
type FormValues = z.infer<typeof schema>;

export default function SignInPage() {
  const { firebaseUser, initialised } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  // Where to go after auth: ?next= (from the guard), else router state, else feed.
  const nextParam = params.get('next');
  const next =
    (nextParam && decodeURIComponent(nextParam)) ||
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ||
    '/feed';

  if (initialised && firebaseUser) {
    return <Navigate to={next} replace />;
  }
  const signUpHref = nextParam ? `/auth/sign-up?next=${nextParam}` : '/auth/sign-up';

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
    } catch (err) {
      toast.error(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setSubmitting(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      toast.error(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to keep scrolling and booking."
      footer={
        <>
          New here?{' '}
          <Link to={signUpHref} className="text-makhe-primary">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label="Email" type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
          error={errors.password?.message}
        />
        <div className="flex items-center justify-between text-xs">
          <Link to="/auth/forgot-password" className="text-makhe-ink/70 hover:text-makhe-ink">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" loading={submitting} className="w-full">
          Sign in
        </Button>
      </form>
      <div className="relative my-2 flex items-center text-xs text-makhe-muted/60">
        <div className="h-px flex-1 bg-makhe-line/50" />
        <span className="px-3">or</span>
        <div className="h-px flex-1 bg-makhe-line/50" />
      </div>
      <Button type="button" variant="secondary" className="w-full" onClick={onGoogle} disabled={submitting}>
        Continue with Google
      </Button>
    </AuthLayout>
  );
}

function asMessage(e: unknown): string {
  return friendlyError(e);
}
